import React, { useState } from 'react';
import { FiTrendingUp, FiBarChart2, FiUsers, FiDollarSign, FiCalendar, FiFileText, FiSettings, FiLogOut } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import SocialConnectModal from './SocialConnectModal';
import brandimage from '../../assets/img/brandsm.png';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { Menu, X } from 'lucide-react';

const SocialMediadashboardSidebar = ({ displayUser, showAccountModal, setShowAccountModal, handleAccountConnected, selectedPlatform }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Handle logout
  const handleLogout = () => {
    // Clear user session data
    localStorage.removeItem('user');
    localStorage.removeItem('socialAccounts');
    localStorage.removeItem('authToken');
    
    // Redirect to home page
    navigate('/');
  };

  // Check if a menu item is active based on current route
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Menu items configuration
  const menuItems = [
    { path: "/Socialmedia/Dashboard", icon: FiTrendingUp, label: "Home" },
    { path: "/Socialmedia/Conent", icon: FiBarChart2, label: "Content" },
    { path: "/Socialmedia/Engagement", icon: FiUsers, label: "Engagement" },
    { path: "/Socialmedia/Campain", icon: FiDollarSign, label: "Campaigns" },
    { path: "/Socialmedia/Ads", icon: FiCalendar, label: "Ads" },
    { path: "/Socialmedia/Reports", icon: FiFileText, label: "Reports" },
  ];

  return (
    <div className={`bg-white shadow-md flex flex-col h-screen transition-all duration-300 ${isCollapsed ? 'w-24' : 'w-64'}`}>
      {/* Header */}
      <div className="p-6 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img src={brandimage} className="h-10 w-10 mr-2" alt="Brand Logo" />
          {!isCollapsed && <h2 className="text-2xl font-bold text-[#FD5F20]">Client Portal</h2>}
        </div>
        <button 
          onClick={toggleSidebar} 
          className="text-gray-600 hover:text-[#FD5F20] transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100"
        >
          {isCollapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      {/* User Info */}
      {displayUser && !isCollapsed && (
        <div className="p-4 border-b border-gray-100 flex items-center bg-gray-50">
          <img
            src={displayUser.picture}
            alt={displayUser.name}
            className="w-10 h-10 rounded-full mr-3 border-2 border-[#FD5F20]"
          />
          <div>
            <p className="font-medium text-sm text-gray-800">{displayUser.name}</p>
            <p className="text-xs text-gray-500">{displayUser.email}</p>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-grow mt-4 px-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <a 
              key={item.path}
              href={item.path}
              className={`flex items-center px-4 py-3 mb-1 rounded-lg transition-all duration-200 ${
                active 
                  ? 'bg-[#FD5F20] text-white shadow-md' 
                  : 'text-gray-600 hover:text-[#FD5F20] hover:bg-orange-50'
              }`}
            >
              <Icon className={`mr-3 ${active ? 'text-white' : 'text-current'}`} size={20} />
              {!isCollapsed && (
                <span className={`font-medium ${active ? 'text-white' : 'text-current'}`}>
                  {item.label}
                </span>
              )}
            </a>
          );
        })}
      </nav>

      {/* Footer Section */}
      <div className="p-4 border-t border-gray-200 mt-auto">
        {/* Settings and Help */}
        {!isCollapsed && (
          <div className="flex items-center justify-between mb-4 text-gray-500">
            <div className="flex items-center space-x-3">
              <button className="p-2 rounded-lg hover:bg-gray-100 hover:text-[#FD5F20] transition-colors duration-200">
                <AiOutlineQuestionCircle size={18} />
              </button>
              <button className="p-2 rounded-lg hover:bg-gray-100 hover:text-[#FD5F20] transition-colors duration-200">
                <FiSettings size={18} />
              </button>
            </div>
            
            {/* Logout Button */}
            <button 
              onClick={handleLogout}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
              title="Logout"
            >
              <FiLogOut className="mr-2" size={18} />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        )}

        {/* Collapsed Footer */}
        {isCollapsed && (
          <div className="flex flex-col items-center space-y-3">
            <button className="p-2 rounded-lg hover:bg-gray-100 hover:text-[#FD5F20] transition-colors duration-200">
              <AiOutlineQuestionCircle size={18} />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 hover:text-[#FD5F20] transition-colors duration-200">
              <FiSettings size={18} />
            </button>
            <button 
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
              title="Logout"
            >
              <FiLogOut size={18} />
            </button>
          </div>
        )}

        {/* Brand Footer */}
        {!isCollapsed && (
          <div className="text-center pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center text-xs text-gray-500">
              <span className="text-gray-500">Powered by</span>
              <span className="font-bold text-[#FD5F20] ml-1">ato creatives</span>
            </div>
          </div>
        )}

        {isCollapsed && (
          <div className="text-center pt-4 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              <span className="font-bold text-[#FD5F20]">ato</span>
            </div>
          </div>
        )}
      </div>

      {/* Connected Accounts Modal */}
      <SocialConnectModal
        show={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        onConnect={handleAccountConnected}
        platform={selectedPlatform}
      />
    </div>
  );
};

export default SocialMediadashboardSidebar;